import { fetchToken } from "@bindery/auth-consumer/browser";
import { useLogger } from "@bindery/logging/react";
import { useCallback, useEffect, useState } from "react";
import { Navigate, useParams } from "react-router";
import { AffiliateShell, DesignSkeleton } from "@/components/design-switcher";
import { readerIdOf, type SessionStore, useSession } from "@/convex/session";
import {
  type AffiliateDeps,
  type AffiliateLoad,
  type HeadlineVariant,
  HEADLINE_TEST,
  HEADLINES,
  headlineVariant,
  loadAffiliate,
} from "@/library/affiliate";
import { getSessionId } from "@/logging";

const defaultDeps: AffiliateDeps = {
  fetch: (input: string, init?: RequestInit) =>
    fetch(input, { credentials: "same-origin", ...init }),
  token: fetchToken,
};

// A reader's variant is seeded by their id, so nothing renders or logs until
// the session answers (or SESSION_WAIT_MS passes, then it seeds from the
// device's session id). The variant is frozen once chosen: a readerId that
// arrives later neither flips the headline nor logs a second impression.
export const SESSION_WAIT_MS = 5000;

export function AffiliatePage({
  deps = defaultDeps,
  sessionStore,
}: {
  deps?: AffiliateDeps;
  sessionStore?: SessionStore;
}) {
  const { readingId = "" } = useParams();
  const session = useSession(sessionStore);
  const [waitedOut, setWaitedOut] = useState(false);
  const sessionSettled = session.status !== "loading" || waitedOut;
  const readerId = readerIdOf(session);
  const log = useLogger();
  const [load, setLoad] = useState<AffiliateLoad | null>(null);
  const [chosen, setChosen] = useState<{
    readingId: string;
    variant: HeadlineVariant;
  } | null>(null);
  const variant = chosen?.readingId === readingId ? chosen.variant : null;

  useEffect(() => {
    if (session.status !== "loading") return;
    const timer = setTimeout(() => setWaitedOut(true), SESSION_WAIT_MS);
    return () => clearTimeout(timer);
  }, [session.status]);

  if (sessionSettled && variant === null) {
    setChosen({
      readingId,
      variant: headlineVariant(readerId ?? getSessionId() ?? readingId),
    });
  }

  useEffect(() => {
    let live = true;
    void loadAffiliate(readingId, deps).then((result) => {
      if (!live) return;
      setLoad(result);
      if (result.kind === "unavailable") {
        log.event("web.affiliate.unavailable", {
          reading_id: readingId,
          reason: result.reason,
        });
      }
    });
    return () => {
      live = false;
    };
  }, [readingId, deps, log]);

  const ready = variant !== null && load?.kind === "ready";
  useEffect(() => {
    if (!ready) return;
    log.event("web.affiliate.impression", {
      test_name: HEADLINE_TEST,
      variant,
      reading_id: readingId,
    });
  }, [ready, variant, readingId, log]);

  const onBuyClick = useCallback(() => {
    log.event("web.affiliate.conversion", {
      test_name: HEADLINE_TEST,
      variant,
      reading_id: readingId,
    });
  }, [log, variant, readingId]);

  if (variant === null || load === null) return <DesignSkeleton />;
  if (load.kind === "unavailable") return <Navigate to="/reader" replace />;
  return (
    <AffiliateShell
      affiliateId={load.affiliateId}
      sku={load.sku}
      bookData={load.bookData}
      readingId={readingId}
      headline={HEADLINES[variant]}
      onBuyClick={onBuyClick}
    />
  );
}
